/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { PasswordField, InputLabel } from 'components/elements';
import { useAuthActions, useReactHookFormErrorMap } from 'hooks';
import { patterns } from 'const';

import Styles from './styles';

const useStyles = makeStyles(Styles);

function PasswordResetForm({ onSubmit, children, tag }) {
  const classes = useStyles();
  const { resetPassword } = useAuthActions();
  const { t } = useTranslation('labels');
  const {
    handleSubmit,
    setError,
    clearErrors,
    control,
    watch,
    getValues,
    formState: { errors },
  } = useForm({
    mode: 'onBlur',
    defaultValues: { password: '', confirmPassword: '' },
  });
  const mapErrorToForm = useReactHookFormErrorMap(getValues, setError, '');
  const passwordRef = useRef({});
  passwordRef.current = watch('password', '');

  useEffect(() => {
    const subscription = watch(() => clearErrors('apiError'));
    return () => subscription.unsubscribe();
  }, [watch]);

  const onSubmitForm = ({ password, confirmPassword }) =>
    resetPassword({ password, confirmPassword, tag })
      .then(() => onSubmit())
      .catch((error) => {
        mapErrorToForm(error);
      });

  return (
    <form className={classes.form} onSubmit={handleSubmit(onSubmitForm)}>
      {children}
      <div className={classes.input}>
        <Controller
          className={classes.control}
          name="password"
          control={control}
          rules={{
            required: t('the_password_you_entered_does_not_meet_the_requirements'),
            minLength: {
              value: 8,
              message: t('the_password_you_entered_does_not_meet_the_requirements'),
            },
            pattern: {
              value: patterns.password,
              message: t('the_password_you_entered_does_not_meet_the_requirements'),
            },
          }}
          render={({ field: { ref, ...restField }, fieldState: { error } }) => (
            <PasswordField
              helperText={error ? error.message : null}
              error={!!error}
              inputProps={{ 'data-testid': 'new-password-input' }}
              {...restField}
              label={<InputLabel text={t('new_password')} required />}
            />
          )}
        />
      </div>
      <div className={classes.input}>
        <Controller
          className={classes.control}
          name="confirmPassword"
          control={control}
          rules={{
            required: t('please_fill_in_this_field'),
            validate: (value) => {
              if (value === passwordRef.current) {
                return true;
              }
              return t('the_passwords_do_not_match');
            },
          }}
          render={({ field: { ref, ...restField }, fieldState: { error } }) => (
            <PasswordField
              helperText={error ? error.message : null}
              inputProps={{ 'data-testid': 'confirm-password-input' }}
              error={!!error}
              {...restField}
              label={<InputLabel text={t('confirm_password')} required />}
            />
          )}
        />
      </div>
      {errors.apiError && <div className={classes.error}>{errors.apiError.message}</div>}
      <Button type="submit" variant="contained" color="primary" className={classes.submit}>
        {t('reset_password')}
      </Button>
    </form>
  );
}

PasswordResetForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  children: PropTypes.element,
  tag: PropTypes.string.isRequired,
};

PasswordResetForm.defaultProps = {
  children: null,
};

export default PasswordResetForm;
